/**
 * Modal para mostrar detalles de rol
 * Sistema Klever Octopus - Betel Natural
 */
import React from 'react';
import PropTypes from 'prop-types';
import { Modal } from '../../components/UI/Modal';

const RoleDetailModal = ({ role, isOpen, onClose, onEdit = null }) => {
  if (!role) return null;

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getLevelLabel = (nivel) => {
    const levels = {
      1: 'Básico - Acceso limitado a funciones esenciales',
      2: 'Intermedio - Acceso a funciones operativas',
      3: 'Avanzado - Acceso a funciones de gestión',
      4: 'Administrador - Acceso completo al sistema'
    };
    return levels[nivel] || 'No definido';
  };

  const getLevelClass = (nivel) => {
    const classes = {
      1: 'level-basic',
      2: 'level-intermediate',
      3: 'level-advanced',
      4: 'level-admin'
    };
    return classes[nivel] || 'level-default';
  };

  // Agrupar permisos por módulo
  const groupedPermissions = role.permissions?.reduce((acc, permission) => {
    const module = permission.module || 'GENERAL';
    if (!acc[module]) {
      acc[module] = [];
    }
    acc[module].push(permission);
    return acc;
  }, {}) || {};

  return (
    <Modal
      title={`Detalles del Rol - ${role.display_name || role.name}`}
      isOpen={isOpen}
      onClose={onClose}
      size="large"
    >
      <div className="role-detail-modal">
        <div className="detail-sections">
          {/* Información Básica */}
          <div className="detail-section">
            <h3 className="section-title">
              <i className="fas fa-info-circle"></i>
              Información Básica
            </h3>
            
            <div className="detail-grid">
              <div className="detail-item">
                <span className="item-label">Nombre para Mostrar</span>
                <span className="item-value">{role.display_name || role.name}</span>
              </div>
              
              <div className="detail-item">
                <span className="item-label">Nombre Interno</span>
                <span className="item-value">
                  <code>{role.name}</code>
                </span>
              </div>
              
              <div className="detail-item">
                <span className="item-label">Estado</span>
                <span className={`item-value ${role.is_active ? 'status-active' : 'status-inactive'}`}>
                  <i className={`fas fa-circle ${role.is_active ? 'text-success' : 'text-danger'}`}></i>
                  {role.is_active ? 'Activo' : 'Inactivo'}
                </span>
              </div>
              
              <div className="detail-item">
                <span className="item-label">Tipo</span>
                <span className="item-value">
                  {role.is_system ? (
                    <span className="system-badge">
                      <i className="fas fa-cogs"></i>
                      Sistema
                    </span>
                  ) : (
                    <span className="custom-badge">
                      <i className="fas fa-user-cog"></i>
                      Personalizado
                    </span>
                  )}
                </span>
              </div>
            </div>

            {role.description && (
              <div className="detail-item full-width">
                <span className="item-label">Descripción</span>
                <span className="item-value">{role.description}</span>
              </div>
            )}
          </div>

          {/* Configuración */}
          <div className="detail-section">
            <h3 className="section-title">
              <i className="fas fa-cogs"></i>
              Configuración
            </h3>
            
            <div className="detail-grid">
              <div className="detail-item">
                <span className="item-label">Nivel de Acceso</span>
                <span className="item-value">
                  <span className={`level-badge ${getLevelClass(role.nivel_acceso)}`}>
                    Nivel {role.nivel_acceso}
                  </span>
                </span>
              </div>
              
              <div className="detail-item">
                <span className="item-label">Color</span>
                <span className="item-value">
                  <div className="color-display">
                    <div 
                      className="color-swatch"
                      style={{ backgroundColor: role.color || '#6c757d' }}
                    ></div>
                    <span>{role.color || '#6c757d'}</span>
                  </div>
                </span>
              </div>
              
              <div className="detail-item">
                <span className="item-label">Usuarios Asignados</span>
                <span className="item-value">
                  <span className="stat-badge">
                    {role.users_count || 0} usuarios
                  </span>
                </span>
              </div>
              
              <div className="detail-item">
                <span className="item-label">Total de Permisos</span>
                <span className="item-value">
                  <span className="stat-badge">
                    {role.permissions?.length || 0} permisos
                  </span>
                </span>
              </div>
            </div>

            <div className="detail-item full-width">
              <span className="item-label">Descripción del Nivel</span>
              <span className="item-value level-description">
                {getLevelLabel(role.nivel_acceso)}
              </span>
            </div>
          </div>

          {/* Permisos */}
          {role.permissions && role.permissions.length > 0 && (
            <div className="detail-section">
              <h3 className="section-title">
                <i className="fas fa-shield-alt"></i>
                Permisos Asignados ({role.permissions.length})
              </h3>
              
              <div className="permissions-by-module">
                {Object.entries(groupedPermissions).map(([module, modulePermissions]) => (
                  <div key={module} className="module-permissions">
                    <h4 className="module-title">
                      <i className={`fas fa-${getModuleIcon(module)}`}></i>
                      {module}
                      <span className="module-count">({modulePermissions.length})</span>
                    </h4>
                    
                    <div className="permissions-grid">
                      {modulePermissions.map(permission => (
                        <div key={permission.id} className="permission-item">
                          <div className="permission-info">
                            <span className="permission-name">
                              {permission.display_name || permission.name}
                            </span>
                            {permission.description && (
                              <small className="permission-description">
                                {permission.description}
                              </small>
                            )}
                          </div>
                          <div className="permission-meta">
                            <code className="permission-code">{permission.name}</code>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Información del Sistema */}
          <div className="detail-section">
            <h3 className="section-title">
              <i className="fas fa-database"></i>
              Información del Sistema
            </h3>
            
            <div className="detail-grid">
              <div className="detail-item">
                <span className="item-label">ID del Rol</span>
                <span className="item-value">#{role.id}</span>
              </div>
              
              <div className="detail-item">
                <span className="item-label">Fecha de Creación</span>
                <span className="item-value">{formatDate(role.created_at)}</span>
              </div>
              
              <div className="detail-item">
                <span className="item-label">Última Actualización</span>
                <span className="item-value">{formatDate(role.updated_at)}</span>
              </div>
              
              <div className="detail-item">
                <span className="item-label">Creado Por</span>
                <span className="item-value">{role.created_by || 'Sistema'}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Acciones */}
        {onEdit && (
          <div className="modal-actions">
            <button
              type="button"
              className="btn btn-primary"
              onClick={onEdit}
            >
              <i className="fas fa-edit"></i>
              Editar Rol
            </button>
          </div>
        )}
      </div>

      <style jsx>{`
        .color-display {
          display: flex;
          align-items: center;
          gap: var(--spacing-sm);
        }

        .color-swatch {
          width: 24px;
          height: 24px;
          border-radius: var(--border-radius-sm);
          border: 1px solid var(--border-color);
        }

        .stat-badge {
          background: var(--color-primary);
          color: white;
          padding: var(--spacing-xs) var(--spacing-sm);
          border-radius: var(--border-radius-sm);
          font-size: var(--font-size-xs);
          font-weight: var(--font-weight-medium);
        }

        .system-badge {
          display: inline-flex;
          align-items: center;
          gap: var(--spacing-xs);
          background: rgba(108, 117, 125, 0.1);
          color: var(--color-gray-600);
          padding: var(--spacing-xs) var(--spacing-sm);
          border-radius: var(--border-radius-sm);
          font-size: var(--font-size-xs);
          font-weight: var(--font-weight-medium);
        }

        .custom-badge {
          display: inline-flex;
          align-items: center;
          gap: var(--spacing-xs);
          background: rgba(40, 167, 69, 0.1);
          color: var(--color-success);
          padding: var(--spacing-xs) var(--spacing-sm);
          border-radius: var(--border-radius-sm);
          font-size: var(--font-size-xs);
          font-weight: var(--font-weight-medium);
        }

        .level-description {
          font-style: italic;
          color: var(--text-secondary);
        }

        .permissions-by-module {
          .module-permissions {
            margin-bottom: var(--spacing-xl);
            
            &:last-child {
              margin-bottom: 0;
            }
          }

          .module-title {
            display: flex;
            align-items: center;
            gap: var(--spacing-sm);
            font-size: var(--font-size-md);
            font-weight: var(--font-weight-medium);
            color: var(--text-primary);
            margin-bottom: var(--spacing-md);
            padding-bottom: var(--spacing-sm);
            border-bottom: 1px solid var(--border-light);

            i {
              color: var(--color-primary);
            }

            .module-count {
              background: var(--color-gray-100);
              color: var(--color-gray-600);
              padding: 2px var(--spacing-xs);
              border-radius: var(--border-radius-sm);
              font-size: var(--font-size-xs);
              font-weight: var(--font-weight-medium);
            }
          }

          .permissions-grid {
            display: grid;
            grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
            gap: var(--spacing-md);
          }

          .permission-item {
            border: 1px solid var(--border-light);
            border-radius: var(--border-radius);
            padding: var(--spacing-md);
            transition: var(--transition);

            &:hover {
              border-color: var(--color-primary);
              box-shadow: var(--shadow-sm);
            }

            .permission-info {
              margin-bottom: var(--spacing-sm);

              .permission-name {
                font-weight: var(--font-weight-medium);
                color: var(--text-primary);
                display: block;
                margin-bottom: var(--spacing-xs);
              }

              .permission-description {
                font-size: var(--font-size-xs);
                color: var(--text-secondary);
                line-height: 1.4;
              }
            }

            .permission-meta {
              .permission-code {
                background: var(--color-gray-100);
                padding: var(--spacing-xs);
                border-radius: var(--border-radius-sm);
                font-size: var(--font-size-xs);
                font-family: monospace;
              }
            }
          }
        }

        .full-width {
          grid-column: 1 / -1;
        }
      `}</style>
    </Modal>
  );
};

// Función auxiliar para obtener iconos de módulos
const getModuleIcon = (module) => {
  const icons = {
    'DASHBOARD': 'tachometer-alt',
    'CLIENTES': 'users',
    'PRODUCTOS': 'box',
    'VENTAS': 'shopping-cart',
    'INVENTARIO': 'warehouse',
    'PROVEEDORES': 'truck',
    'PRODUCCION': 'industry',
    'VENDEDORES': 'user-tie',
    'ORDENES': 'file-alt',
    'REPORTES': 'chart-bar',
    'SEGUIMIENTO': 'search',
    'ADMIN': 'cogs'
  };
  
  return icons[module] || 'folder';
};

RoleDetailModal.propTypes = {
  role: PropTypes.object,
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onEdit: PropTypes.func
};

export default RoleDetailModal;
